/** @format */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';  // ✅ Ajouté pour vérifier le rôle
import { 
  Edit3, 
  Eye, 
  Trash2, 
  Plus, 
  Search,
  Calendar,
  User,
  Car,
  Wrench,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader as LoaderIcon
} from 'lucide-react';
import { motion } from 'framer-motion';

const InterventionsList = () => {
  const navigate = useNavigate();
  const { user, userRole } = useAuth();  // ✅ Récupérer l'utilisateur et son rôle
  const [selectedIntervention, setSelectedIntervention] = useState(null);  // ✅ Pour la modal détails
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Charger les interventions au montage du composant
  useEffect(() => {
    loadInterventions();
  }, []);

  const loadInterventions = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Chargement des interventions...');

      const { data, error } = await supabase
        .from('interventions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      console.log('✅ Interventions chargées:', data?.length || 0);
      setInterventions(data || []);
    } catch (error) {
      console.error('❌ Erreur chargement interventions:', error);
      setError('Erreur lors du chargement des interventions');
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour modifier une intervention
  const handleEdit = (intervention) => {
    console.log('📝 Édition intervention:', intervention);
    navigate('/pole-auto/interventions/form', {
      state: { intervention: intervention }
    });
  };

  // Fonction pour voir les détails
  const handleView = (intervention) => {
    console.log('👁️ Voir détails:', intervention);
    setSelectedIntervention(intervention);  // ✅ Ouvrir la modal
  };

  // Fonction pour supprimer une intervention
  const handleDelete = async (id, immatriculation) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer l'intervention ${immatriculation} ?`)) {
      return;
    }

    try {
      console.log('🗑️ Suppression intervention:', id);

      const { error } = await supabase
        .from('interventions')
        .delete()
        .eq('id', id);

      if (error) throw error;

      console.log('✅ Intervention supprimée');
      loadInterventions();
    } catch (error) {
      console.error('❌ Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  // Filtrer les interventions
  const filteredInterventions = interventions.filter((intervention) => {
    const matchesSearch = 
      intervention.immatriculation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      intervention.client_name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = 
      filterStatus === 'all' || 
      intervention.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  // Fonction pour obtenir l'icône de statut
  const getStatusIcon = (status) => {
    switch (status) {
      case 'terminee':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'en_cours':
        return <Clock className="w-5 h-5 text-blue-400" />;
      case 'planifiee':
        return <Calendar className="w-5 h-5 text-yellow-400" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  // Fonction pour obtenir le badge de statut
  const getStatusBadge = (status) => {
    switch (status) {
      case 'terminee':
        return 'bg-green-500/20 text-green-300 border-green-500';
      case 'en_cours':
        return 'bg-blue-500/20 text-blue-300 border-blue-500';
      case 'planifiee':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500';
    }
  };

  // Fonction pour formater la date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Affichage du chargement
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <LoaderIcon className="w-12 h-12 text-blue-400 animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Chargement des interventions...</p>
        </div>
      </div>
    );
  }

  // Affichage de l'erreur
  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
        <div className="bg-red-900/20 border border-red-600 rounded-lg p-8 max-w-lg">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2 text-center">Erreur</h2>
          <p className="text-red-300 text-center mb-4">{error}</p>
          <button
            onClick={loadInterventions}
            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* En-tête */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                Interventions Pédagogiques
              </h1>
              <p className="text-gray-400">
                {filteredInterventions.length} intervention{filteredInterventions.length > 1 ? 's' : ''} trouvée{filteredInterventions.length > 1 ? 's' : ''}
              </p>
            </div>
            <button
              onClick={() => navigate('/pole-auto/interventions/new')}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center space-x-2 transition-colors shadow-lg"
            >
              <Plus className="w-5 h-5" />
              <span className="font-semibold">Nouvelle intervention</span>
            </button>
          </div>
        </div>

        {/* Filtres et recherche */}
        <div className="bg-slate-800 rounded-lg p-4 mb-6 shadow-xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Barre de recherche */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Rechercher par immatriculation ou client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
              />
            </div>

            {/* Filtre par statut */}
            <div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
              >
                <option value="all">Tous les statuts</option>
                <option value="planifiee">Planifiée</option>
                <option value="en_cours">En cours</option>
                <option value="terminee">Terminée</option>
              </select>
            </div>
          </div>
        </div>

        {/* Liste des interventions */}
        {filteredInterventions.length === 0 ? (
          <div className="bg-slate-800 rounded-lg p-12 text-center shadow-xl">
            <Car className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">
              {searchTerm || filterStatus !== 'all' 
                ? 'Aucune intervention trouvée avec ces critères' 
                : 'Aucune intervention enregistrée'}
            </p>
            {!searchTerm && filterStatus === 'all' && (
              <button
                onClick={() => navigate('/pole-auto/interventions/new')}
                className="mt-4 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Créer la première intervention
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredInterventions.map((intervention, index) => (
              <motion.div
                key={intervention.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-slate-800 rounded-lg p-6 hover:bg-slate-700 transition-all shadow-xl border border-slate-700 hover:border-blue-500"
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  {/* Informations principales */}
                  <div className="flex-1 space-y-3">
                    {/* Ligne 1: Immatriculation + Statut */}
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center space-x-3">
                        <Car className="w-6 h-6 text-blue-400" />
                        <h3 className="text-2xl font-bold text-white">
                          {intervention.immatriculation || 'N/A'}
                        </h3>
                      </div>
                      <div className={`px-3 py-1 rounded-full border text-sm font-semibold flex items-center space-x-2 ${getStatusBadge(intervention.status)}`}>
                        {getStatusIcon(intervention.status)}
                        <span className="capitalize">
                          {intervention.status?.replace('_', ' ') || 'Non défini'}
                        </span>
                      </div>
                    </div>

                    {/* Ligne 2: Client */}
                    <div className="flex items-center space-x-2 text-gray-300">
                      <User className="w-5 h-5 text-gray-400" />
                      <span className="font-medium">
                        {intervention.client_name || 'Client inconnu'}
                      </span>
                    </div>

                    {/* Ligne 3: Type d'intervention */}
                    <div className="flex items-center space-x-2 text-gray-300">
                      <Wrench className="w-5 h-5 text-gray-400" />
                      <span>
                        Type: {intervention.intervention_type || 'Non spécifié'}
                      </span>
                    </div>

                    {/* Ligne 4: Dates */}
                    <div className="flex items-center space-x-2 text-gray-300">
                      <Calendar className="w-5 h-5 text-gray-400" />
                      <span>
                        Date: {formatDate(intervention.completion_date)}
                      </span>
                    </div>

                    {/* Description (tronquée) */}
                    {intervention.work_description && (
                      <p className="text-gray-400 text-sm mt-2 line-clamp-2">
                        {intervention.work_description}
                      </p>
                    )}

                    {/* Formateur */}
                    {intervention.trainer_name && (
                      <div className="flex items-center space-x-2 text-gray-400 text-sm">
                        <User className="w-4 h-4" />
                        <span>Formateur: {intervention.trainer_name}</span>
                      </div>
                    )}
                  </div>

                  {/* Boutons d'action */}
                  <div className="flex lg:flex-col gap-2">
                    {/* Bouton Modifier ⭐ */}
                    <button
                      onClick={() => handleEdit(intervention)}
                      className="flex-1 lg:flex-none px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center space-x-2 transition-colors shadow-lg"
                      title="Modifier cette intervention"
                    >
                      <Edit3 className="w-4 h-4" />
                      <span className="font-medium">Modifier</span>
                    </button>

                    {/* Bouton Voir */}
                    <button
                      onClick={() => handleView(intervention)}
                      className="flex-1 lg:flex-none px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg flex items-center justify-center space-x-2 transition-colors"
                      title="Voir les détails"
                    >
                      <Eye className="w-4 h-4" />
                      <span className="font-medium">Voir</span>
                    </button>

                    {/* Bouton Supprimer - Réservé au super_admin */}
                    {userRole === 'super_admin' && (
                      <button
                        onClick={() => handleDelete(intervention.id, intervention.immatriculation)}
                        className="flex-1 lg:flex-none px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center justify-center space-x-2 transition-colors"
                        title="Supprimer cette intervention"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="font-medium">Supprimer</span>
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

        {/* Modal de détails */}
        {selectedIntervention && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedIntervention(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-slate-800 rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center space-x-3">
                  <Car className="w-8 h-8 text-blue-400" />
                  <span>{selectedIntervention.immatriculation}</span>
                </h2>
                <button
                  onClick={() => setSelectedIntervention(null)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-400 text-sm">Client</p>
                    <p className="text-white font-semibold">{selectedIntervention.client_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Statut</p>
                    <div className={`inline-flex px-3 py-1 rounded-full border text-sm font-semibold ${getStatusBadge(selectedIntervention.status)}`}>
                      {selectedIntervention.status?.replace('_', ' ') || 'Non défini'}
                    </div>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Type d'intervention</p>
                    <p className="text-white">{selectedIntervention.intervention_type || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Date</p>
                    <p className="text-white">{formatDate(selectedIntervention.completion_date)}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Formateur</p>
                    <p className="text-white">{selectedIntervention.trainer_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Créée le</p>
                    <p className="text-white">{formatDate(selectedIntervention.created_at)}</p>
                  </div>
                </div>

                <div>
                  <p className="text-gray-400 text-sm mb-2">Description des travaux</p>
                  <p className="text-white bg-slate-700 p-4 rounded-lg">
                    {selectedIntervention.work_description || 'Aucune description'}
                  </p>
                </div>

                {selectedIntervention.future_maintenance && (
                  <div>
                    <p className="text-gray-400 text-sm mb-2">Entretien futur</p>
                    <p className="text-white bg-slate-700 p-4 rounded-lg">
                      {selectedIntervention.future_maintenance}
                    </p>
                  </div>
                )}

                {selectedIntervention.apprentices && selectedIntervention.apprentices.length > 0 && (
                  <div>
                    <p className="text-gray-400 text-sm mb-2">Apprentis ({selectedIntervention.apprentices.length})</p>
                    <div className="space-y-2">
                      {selectedIntervention.apprentices.map((apprenti, index) => (
                        <div key={index} className="bg-slate-700 p-3 rounded-lg">
                          <p className="text-white font-medium">{apprenti.name}</p>
                          <p className="text-gray-400 text-sm">{apprenti.groupe_name || apprenti.groupe}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => {
                    setSelectedIntervention(null);
                    handleEdit(selectedIntervention);
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center space-x-2 transition-colors"
                >
                  <Edit3 className="w-4 h-4" />
                  <span>Modifier</span>
                </button>
                <button
                  onClick={() => setSelectedIntervention(null)}
                  className="flex-1 px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg transition-colors"
                >
                  Fermer
                </button>
              </div>
            </motion.div>
          </div>
        )}
    </div>
  );
};

export default InterventionsList;